import Hero from '../components/Hero';

const Standards = () => {
  const competencies = [
    {
      category: 'A',
      title: 'Foundation of Conscious Practice',
      description: 'The inner ground from which all coaching competence grows.',
      items: [
        { code: 'A1', title: 'Conscious Presence', description: 'Full, non-reactive attention in service of the client, the foundation of all other competencies.' },
        { code: 'A2', title: 'Emotional Intelligence Mastery', description: 'Working skillfully with emotion in self and client, including recognition, regulation, and empathic attunement.' },
        { code: 'A3', title: 'Personal Inner Development', description: 'Ongoing engagement with the coach’s own beliefs, biases, conditioning, and growth edges.' }
      ]
    },
    {
      category: 'B',
      title: 'Core Coaching Capabilities',
      description: 'The professional skills and knowledge that make coaching effective and safe.',
      items: [
        { code: 'B4', title: 'Neuroscience-Informed Coaching', description: 'Understanding why techniques work at a neurological level, enabling more precise and effective application.' },
        { code: 'B5', title: 'Somatic & Nervous System Awareness', description: 'Working with the body as a source of information and change, including titration and grounding.' },
        { code: 'B6', title: 'Evidence Literacy & Ethical Claims Practice', description: 'Representing the evidence for one’s work honestly and accurately, the competency that defines ICCAC’s approach.' },
        { code: 'B7', title: 'Coaching Ethics & Scope of Practice', description: 'Maintaining clear professional boundaries and referring appropriately to clinical support when needed.' }
      ]
    },
    {
      category: 'C',
      title: 'Depth & Transformation',
      description: 'The advanced capabilities that create profound and lasting change.',
      items: [
        { code: 'C8', title: 'Transformational Facilitation', description: 'Facilitating genuine change at the level of identity, belief, and meaning, beyond behavioral adjustment.' },
        { code: 'C9', title: 'Cultural & Wisdom Intelligence', description: 'Engaging respectfully and accurately with diverse wisdom traditions, including accurate representation of their evidence basis.' },
        { code: 'C10', title: 'Results & Transformation Measurement', description: 'Tracking, documenting, and communicating outcomes honestly, aligned with the evidence tier of the modality used.' }
      ]
    }
  ];

  const claimsPolicy = [
    {
      tier: 'Tier 1',
      language: 'Research consistently demonstrates. Studies show. Clinically validated. Evidence consistently supports.',
      example: '"Research consistently demonstrates that mindfulness practice reduces stress biomarkers. I can share the studies with you."'
    },
    {
      tier: 'Tier 2',
      language: 'Early research suggests. Initial evidence indicates. This is a developing area of research.',
      example: '"Early research in this area is promising. This is a developing field and I want to be transparent about that."'
    },
    {
      tier: 'Tier 3',
      language: 'Rooted in the Vedantic tradition. Practitioners consistently report. A contemplative model rather than a clinical intervention.',
      example: '"This practice is rooted in the Vedantic tradition, developed over thousands of years. It is a contemplative model, and I want you to understand that clearly."'
    }
  ];

  const ethicsArticles = [
    { num: 1, title: 'Client Primacy', description: 'The coach’s primary obligation is to the wellbeing and best interests of the client, above all commercial or personal considerations.' },
    { num: 2, title: 'Informed Consent', description: 'Full informed consent including evidence tier disclosure is obtained before commencing coaching.' },
    { num: 3, title: 'Confidentiality', description: 'Client information is held in strict confidence except in defined circumstances required by law.' },
    { num: 4, title: 'Professional Boundaries', description: 'Clear professional boundaries are maintained in all coaching relationships without exception.' },
    { num: 5, title: 'Scope of Practice', description: 'The coach practices only within the boundaries of their training and competence, and refers appropriately.' },
    { num: 6, title: 'Honesty & Integrity', description: 'The coach represents their qualifications and the evidence for their work accurately at all times.' },
    { num: 7, title: 'Continuing Development', description: 'The coach maintains professional competence through ongoing CPD, supervision, and annual evidence literacy updates.' },
    { num: 8, title: 'Reporting Obligations', description: 'The coach reports concerns about fellow credential holders\' conduct to ICCAC through the formal complaints process.' }
  ];

  return (
    <div>
      <Hero
        title="Non-Negotiable Standards"
        subtitle="Transparent Ethics"
      />

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 text-center max-w-4xl mx-auto mb-12">
            ICCAC holds its coaches and accredited programs to the highest global standards of professional practice, 
            ethical conduct, and client safety. These standards are not aspirational. They are binding conditions of every credential.
          </p>
        </div>
      </div>

      {/* Competency Framework */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4 text-center">Competency Framework</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            The ICCAC Consciousness Coaching Competency Framework (CCCF)
          </p>

          <div className="space-y-8">
            {competencies.map((category) => (
              <div key={category.category} className="bg-white rounded-lg shadow-md p-8">
                <div className="flex items-center mb-4">
                  <div className="text-4xl font-bold text-[#1e3a5f] mr-4">{category.category}</div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#1e3a5f]">{category.title}</h3>
                    <p className="text-gray-600">{category.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  {category.items.map((item) => (
                    <div key={item.code} className="bg-gray-50 p-4 rounded">
                      <div className="font-bold text-[#1e3a5f] mb-1">{item.code}</div>
                      <div className="font-semibold text-gray-800 mb-1">{item.title}</div>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Evidence Claims Policy */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4 text-center">Evidence Claims Policy</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            What ICCAC Coaches May and May Not Claim
          </p>

          <div className="space-y-6">
            {claimsPolicy.map((policy) => (
              <div key={policy.tier} className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{policy.tier}</h3>
                <div className="mb-4">
                  <span className="font-semibold text-gray-700">Approved Language: </span>
                  <span className="text-gray-600">{policy.language}</span>
                </div>
                <div className="bg-blue-50 p-4 rounded border-l-4 border-[#1e3a5f]">
                  <span className="font-semibold text-gray-700">Example: </span>
                  <span className="italic text-gray-700">{policy.example}</span>
                </div>
              </div>
            ))}
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-red-700 mb-3">Prohibited for All Credential Holders</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Claims that coaching cures or treats medical conditions</li>
                <li>Representing Tier 2 or 3 as clinically proven</li>
                <li>Guaranteeing specific outcomes</li>
                <li>Claiming NLP eye-accessing cues are validated by neuroscience</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Code of Ethics */}
      <div className="py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5380] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-center">Code of Ethics</h2>
          <p className="text-xl text-gray-200 text-center mb-12">Eight Articles. Non-Negotiable.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ethicsArticles.map((article) => (
              <div key={article.num} className="bg-white/10 backdrop-blur p-6 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-white text-[#1e3a5f] rounded-full flex items-center justify-center font-bold mr-4">
                    {article.num}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{article.title}</h3>
                    <p className="text-gray-200">{article.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Standards;